import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import InternalLinks from '../components/InternalLinks';
import { 
  Building2, 
  Search, 
  FileText, 
  AlertTriangle, 
  Clock, 
  CheckCircle,
  ArrowRight,
  HardHat,
  Truck,
  Wrench,
  TrendingUp,
  Target
} from 'lucide-react';

export default function MarchesPublicsBtpPage() {
  return (
    <>
      <SEO 
        title="Marchés Publics BTP : Trouver des Appels d'Offres dans le Bâtiment"
        description="Comment trouver et remporter des marchés publics dans le BTP. Guide complet : sources d'appels d'offres bâtiment, analyse DCE travaux, erreurs à éviter. Conseils pour entreprises de construction."
        path="/marches-publics-btp"
      />
      <div className="min-h-screen bg-white">
        <Header />
        
        <Breadcrumb 
          items={[
            { label: 'Marchés publics BTP' }
          ]} 
        />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-orange-50 via-amber-50 to-white py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-6">
                <HardHat className="w-4 h-4" />
                Secteur BTP & Construction
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Marchés Publics BTP : Comment Trouver des Appels d'Offres dans le Bâtiment
              </h1>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
                Le secteur du BTP représente plus de 40% des marchés publics en France. 
                Découvrez comment identifier les opportunités, analyser les DCE travaux et 
                maximiser vos chances de remporter des contrats publics.
              </p>
              <Link to="/">
                <Button size="lg" className="bg-orange-600 hover:bg-orange-700">
                  <Search className="w-5 h-5 mr-2" />
                  Trouver des marchés BTP
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                { value: '40%', label: 'des marchés publics', icon: Building2 },
                { value: '50 Md€', label: 'de commande publique BTP/an', icon: TrendingUp },
                { value: '70%', label: 'attribués aux PME', icon: Target },
                { value: '200+', label: 'nouveaux marchés/jour', icon: FileText }
              ].map((stat, i) => (
                <div key={`item-${i}`} className="bg-white rounded-xl p-6 text-center shadow-sm">
                  <stat.icon className="w-8 h-8 text-orange-600 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-sm text-slate-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            
            {/* Section 1 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Building2 className="w-8 h-8 text-orange-600" />
                Quels types de marchés publics dans le BTP ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p>
                  Le secteur du <strong>bâtiment et travaux publics</strong> couvre une grande variété de marchés publics, 
                  allant de la construction neuve à la rénovation énergétique. Voici les principales catégories :
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 mt-6">
                {[
                  { title: 'Construction neuve', desc: 'Bâtiments publics, écoles, hôpitaux, logements sociaux', icon: Building2 },
                  { title: 'Rénovation & réhabilitation', desc: 'Mise aux normes, rénovation énergétique, accessibilité', icon: Wrench },
                  { title: 'Travaux de voirie', desc: 'Routes, trottoirs, aménagements urbains, parkings', icon: Truck },
                  { title: 'Lots techniques', desc: 'Électricité, plomberie, CVC, menuiserie, peinture', icon: HardHat }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="bg-orange-50 rounded-xl p-5 border border-orange-100">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-orange-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{item.title}</h3>
                        <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mt-6 rounded-r-lg">
                <p className="text-amber-800">
                  <strong>Bon à savoir :</strong> Les marchés de travaux sont souvent allotis (divisés en lots). 
                  Une PME peut ainsi candidater sur un lot spécifique (ex: lot électricité) sans avoir à répondre sur l'ensemble du projet.
                </p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Search className="w-8 h-8 text-orange-600" />
                Où trouver les appels d'offres BTP ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600 mb-6">
                <p>
                  Pour ne manquer aucune opportunité dans le bâtiment, il est essentiel de surveiller plusieurs sources officielles. 
                  Voici les principales plateformes où sont publiés les <strong>marchés publics de travaux</strong> :
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { 
                    name: 'BOAMP', 
                    desc: 'Bulletin Officiel des Annonces des Marchés Publics - Source officielle pour tous les marchés nationaux',
                    url: 'boamp.fr'
                  },
                  { 
                    name: 'DECP', 
                    desc: 'Données Essentielles de la Commande Publique - Données ouvertes sur les marchés attribués',
                    url: 'data.gouv.fr'
                  },
                  { 
                    name: 'Profils acheteurs', 
                    desc: 'Plateformes des collectivités (AWS, Atexo, Dematis, Maximilien...)',
                    url: 'Multiples plateformes'
                  },
                  { 
                    name: 'TED Europa', 
                    desc: 'Marchés européens dépassant les seuils communautaires',
                    url: 'ted.europa.eu'
                  }
                ].map((source, i) => (
                  <div key={`item-${i}`} className="flex items-start gap-4 p-4 bg-white border border-slate-200 rounded-xl hover:border-orange-300 transition-colors">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900">{source.name}</h3>
                      <p className="text-slate-600 text-sm">{source.desc}</p>
                      <span className="text-xs text-slate-400">{source.url}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-orange-600 text-white rounded-2xl p-6 mt-8">
                <h3 className="text-xl font-bold mb-3">Gagnez du temps avec une veille automatisée</h3>
                <p className="text-orange-100 mb-4">
                  Plutôt que de consulter chaque source manuellement, utilisez une solution de 
                  <Link to="/veille-marches-publics" className="underline font-medium text-white"> veille marchés publics automatisée</Link> qui 
                  agrège toutes les sources et vous alerte sur les opportunités correspondant à votre secteur.
                </p>
                <Link to="/">
                  <Button variant="secondary" className="bg-white text-orange-600 hover:bg-orange-50">
                    Essayer gratuitement
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <FileText className="w-8 h-8 text-orange-600" />
                Comment analyser un DCE dans le bâtiment ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600 mb-6">
                <p>
                  Le <strong>Dossier de Consultation des Entreprises (DCE)</strong> dans le BTP est souvent volumineux. 
                  Voici une méthode pour l'analyser efficacement et identifier rapidement si le marché vous correspond :
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { step: '1', title: 'Règlement de Consultation (RC)', desc: 'Critères d\'attribution, pondération prix/technique, délai de remise' },
                  { step: '2', title: 'CCAP', desc: 'Conditions administratives, pénalités de retard, garanties demandées' },
                  { step: '3', title: 'CCTP', desc: 'Cahier technique détaillant les prestations attendues lot par lot' },
                  { step: '4', title: 'Plans et annexes', desc: 'Plans architecte, études techniques, diagnostics existants' },
                  { step: '5', title: 'BPU / DQE', desc: 'Bordereau des Prix Unitaires et Détail Quantitatif Estimatif à compléter' }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1 bg-slate-50 rounded-xl p-4">
                      <h3 className="font-semibold text-slate-900">{item.title}</h3>
                      <p className="text-slate-600 text-sm mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-slate-600 mt-6">
                Pour gagner du temps, notre <Link to="/analyse-dce" className="text-orange-600 font-medium hover:underline">outil d'analyse DCE par IA</Link> extrait 
                automatiquement les informations clés d'un dossier de consultation en quelques secondes.
              </p>
            </div>

            {/* Section 4 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-orange-600" />
                Les erreurs fréquentes des entreprises du BTP
              </h2>
              
              <div className="space-y-4">
                {[
                  { 
                    error: 'Candidater sans vérifier les critères d\'exclusion',
                    solution: 'Toujours vérifier les références exigées, qualifications requises (Qualibat, RGE...) et capacités financières minimales'
                  },
                  { 
                    error: 'Négliger le mémoire technique',
                    solution: 'Le prix ne fait pas tout : un mémoire technique bien rédigé peut faire la différence face à un concurrent moins-disant'
                  },
                  { 
                    error: 'Sous-estimer les délais administratifs',
                    solution: 'Prévoir les délais pour obtenir attestations fiscales, sociales, et certificats de qualifications'
                  },
                  { 
                    error: 'Oublier de visiter le site',
                    solution: 'La visite de site est souvent obligatoire et permet d\'identifier des contraintes non mentionnées dans le DCE'
                  },
                  { 
                    error: 'Répondre à tous les marchés sans cibler',
                    solution: 'Privilégier la qualité des réponses sur des marchés ciblés plutôt que la quantité'
                  }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-red-600 font-bold text-sm">✗</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{item.error}</h3>
                        <div className="flex items-start gap-2 mt-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                          <p className="text-slate-600 text-sm">{item.solution}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 5 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Clock className="w-8 h-8 text-orange-600" />
                Comment gagner du temps dans la veille BTP ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600 mb-6">
                <p>
                  Une entreprise du BTP peut passer plusieurs heures par jour à rechercher des appels d'offres. 
                  Voici comment optimiser ce processus :
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-100 rounded-xl p-6">
                  <h3 className="font-bold text-slate-900 mb-4 text-red-600">❌ Méthode traditionnelle</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500">•</span>
                      Consulter 5-10 plateformes chaque jour
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500">•</span>
                      Télécharger et lire chaque DCE manuellement
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500">•</span>
                      2-3 heures/jour de veille
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500">•</span>
                      Risque de manquer des opportunités
                    </li>
                  </ul>
                </div>
                
                <div className="bg-orange-50 rounded-xl p-6 border-2 border-orange-200">
                  <h3 className="font-bold text-slate-900 mb-4 text-green-600">✓ Avec Mon DCE Express</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                      Toutes les sources centralisées
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                      Synthèse IA du DCE en 30 secondes
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                      15 minutes/jour suffisent
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                      Alertes personnalisées par email
                    </li>
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-orange-600 to-amber-600 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Prêt à trouver vos prochains marchés BTP ?
            </h2>
            <p className="text-orange-100 mb-8 max-w-xl mx-auto">
              Essayez gratuitement et recevez 3 recommandations de marchés publics 
              adaptés à votre activité dans le bâtiment.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-white text-orange-600 hover:bg-orange-50">
                <HardHat className="w-5 h-5 mr-2" />
                Commencer ma veille BTP gratuite
              </Button>
            </Link>
          </div>
        </section>

        {/* Internal Links */}
        <InternalLinks currentPage="btp" />

        <Footer />
      </div>
    </>
  );
}
